import clsx from 'clsx'
import { HidingHeader, usePauseHidingHeader, useRunHidingHeader } from 'hiding-header-react'
import { useRouter } from 'next/dist/client/router'
import Link from 'next/link'
import React, { useCallback, useEffect, useState, type ChangeEventHandler } from 'react'
import { useIsAllowedToFullUse } from '../../../hooks/useIsAllowedToFullUse'
import { useLocalizedSignIn } from '../../../hooks/useLocalizedSignIn'
import { createFetcher } from '../../../libs/next/api/jsonApiHandler'
import type { API } from '../../../pages/api/forms/[handler]'
import type { API as UserAPI } from '../../../pages/api/user/[handler]'
import { InternalLink } from '../../const/Links'
import { menuItems } from '../../const/MenuItems'
import { Translation, useTranslate } from '../../contexts/TranslationContext'
import SvgEnvelope from '../../svg/icons/Envelope'
import SvgHamburger from '../../svg/icons/Hamburger'
import SvgSearch from '../../svg/icons/Search'
import SvgUserAdd from '../../svg/icons/UserAdd'
import SvgWarningTriangle from '../../svg/icons/WarningTriangle'
import { useAuth } from '../../user/UserContext'
import { usePageLoadingState } from '../../utils/usePageLoadingState'
import { TextInput } from '../form/TextInput'
import { Button } from '../shared/Button'
import { Collapsible } from '../shared/Collapsible'
import { Container } from '../shared/Container'
import { InfoModal } from '../shared/InfoModal'
import { Logo } from '../shared/Logo'
import { Message } from '../shared/Message'
import { MobileMenu } from '../shared/MobileMenu'
import { SearchModal } from '../shared/SearchModal'
import { UserMenu } from '../shared/UserMenu'
import style from './Header.module.sass'

const api = createFetcher<API>('/api/forms/[handler]')
const userApi = createFetcher<UserAPI>('/api/user/[handler]')

export const Header = React.memo(function Header() {
	const runHidingHeader = useRunHidingHeader()
	const pauseHidingHeader = usePauseHidingHeader()
	const [loading] = usePageLoadingState()

	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
	const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)

	useEffect(() => {
		if (isMobileMenuOpen || isSearchModalOpen) {
			pauseHidingHeader && pauseHidingHeader()
		} else {
			runHidingHeader && runHidingHeader()
		}
	}, [loading, runHidingHeader, pauseHidingHeader, isMobileMenuOpen, isSearchModalOpen])

	const auth = useAuth()
	const loggedUser = auth.status === 'signedIn'

	const [searchValue, setSearchValue] = useState('')
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
	const [newsletterEmail, setNewsletterEmail] = useState<string | undefined>(undefined)
	const [isNewsletterModalOpen, setIsNewsletterModalOpen] = useState(false)
	const [isConfirmResendModalOpen, setIsConfirmResendModalOpen] = useState(false)
	const [wrongFormatAlertVisible, setWrongFormatAlertVisible] = useState(false)
	const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
		setSearchValue(event.currentTarget.value)
	}

	const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)

	const fullUse = useIsAllowedToFullUse()
	const handleClose = useCallback(() => {
		setIsSearchModalOpen(false)
		setSearchValue('')
		setWrongFormatAlertVisible(false)
	}, [])

	const { signIn } = useLocalizedSignIn()

	const router = useRouter()

	const isVerifyPage = router.asPath.includes(InternalLink.Protected)

	const authLoading = auth.status === 'loading'

	const translate = useTranslate()
	const checkEmail = useCallback((value: string) => {
		const reg1 = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/
		if (value !== '' && reg1.test(value) == false) {
			setWrongFormatAlertVisible(true)
		}
	}, [])

	const closeUserMenu = useCallback(() => {
		setIsUserMenuOpen(false)
	}, [])

	const [isLoading, setIsLoading] = useState(false)

	const closeNewsletterModal = useCallback(() => {
		setIsNewsletterModalOpen(false)
		setNewsletterEmail(loggedUser && auth.user?.email ? auth.user.email : undefined)
		setWrongFormatAlertVisible(false)
	}, [auth, loggedUser])

	const toggleUserMenu = useCallback(() => setIsUserMenuOpen(!isUserMenuOpen), [isUserMenuOpen])

	const openNewsletterModal = useCallback(() => {
		setIsNewsletterModalOpen(true)
		if (loggedUser && auth.user?.email) {
			setNewsletterEmail(auth.user.email)
		}
	}, [auth, loggedUser])

	const openSearchModal = useCallback(() => setIsSearchModalOpen(true), [])
	const openMobileMenu = useCallback(() => setIsMobileMenuOpen(true), [])

	const content = (
		<div className={style.header}>
			<header className={clsx(style.headerWrapper, isMobileMenuOpen && style.headerWrapperBg)}>
				<div className={style.logoWrapper}>
					<Logo />
				</div>
				<ul className={style.menuItems}>
					{menuItems.map(
						(item, index) =>
							item.link && (
								<li key={item.link}>
									<Link
										href={item.link}
										title={item.name}
										className={clsx(
											style.menuItemWrapper,
											index === 0 && style.menuItemWrapperRed,
											item.paths?.find((path) => router.pathname.includes(path)) && style.menuItemWrapperIsActive,
											index === 0 && style.menuItemWrapperRedIsActive,
										)}
									>
										{item.label}
									</Link>
								</li>
							),
					)}
				</ul>
				<div className={style.personal}>
					<div className={clsx(style.personalWrapper, isMobileMenuOpen && style.personalWrapperNoBox)}>
						{!isMobileMenuOpen && (
							<>
								<div
									className={clsx(
										style.userIconWrapper,
										isUserMenuOpen && style.userIconWrapperNoHover,
										isUserMenuOpen && style.userIconWrapperSmaller,
									)}
								>
									{authLoading ? (
										<div className={style.loader} />
									) : loggedUser ? (
										<button
											type="button"
											aria-label={
												auth.user?.fullName
													? auth.user?.fullName.charAt(0).toUpperCase()
													: auth.user?.email.charAt(0).toUpperCase()
											}
											className={clsx(style.icon, isUserMenuOpen && style.iconColored)}
											onClick={toggleUserMenu}
										>
											{auth.user?.fullName
												? auth.user?.fullName.charAt(0).toUpperCase()
												: auth.user?.email.charAt(0).toUpperCase()}
										</button>
									) : (
										<Button
											type={{ variant: 'nakedIcon' }}
											hoverBackground
											icon={{ aloneStanding: true }}
											label={<SvgUserAdd />}
											ariaLabel={translate('header.login.button')}
											onClick={() => signIn()}
										/>
									)}
								</div>
								<div className={style.newsletterIconWrapper}>
									<Button
										type={{ variant: 'nakedIcon' }}
										hoverBackground
										icon={{ aloneStanding: true }}
										label={<SvgEnvelope />}
										ariaLabel={translate('header.newsletter.button')}
										onClick={openNewsletterModal}
									/>
								</div>
								<div className={style.searchIconWrapper}>
									<Button
										label={<SvgSearch />}
										ariaLabel={translate('header.search.button')}
										icon={{ aloneStanding: true }}
										type={{ variant: 'transparent' }}
										onClick={openSearchModal}
										hoverBackground
									/>
								</div>
							</>
						)}
						<div className={clsx(style.hamburgerIconWrapper, isMobileMenuOpen && style.crossIcon)}>
							<Button
								label={<SvgHamburger />}
								ariaLabel={translate('header.menu.open.button')}
								icon={{ aloneStanding: true }}
								type={{ variant: 'transparent' }}
								onClick={openMobileMenu}
								hoverBackground
							/>
						</div>
						{isUserMenuOpen && (
							<div className={style.userMenu}>
								<UserMenu close={closeUserMenu} />
							</div>
						)}
					</div>
				</div>
			</header>
		</div>
	)

	const { preview } = router.query

	return (
		<>
			{preview && <div className={style.draft}>Draft Mode</div>}
			{router.asPath === InternalLink.Comparator ? content : <HidingHeader>{content}</HidingHeader>}
			{isNewsletterModalOpen && (
				<InfoModal
					title={translate('infoModal.newsletterModal.title')}
					lead={translate('infoModal.newsletterModal.subtitle')}
					imageUrl="/images/pejsek.jpg"
					imageAlternativeText={translate('infoModal.newsletterModal.alt.image')}
					closeModal={closeNewsletterModal}
					isOpen={isNewsletterModalOpen}
					content={
						<form
							action=""
							method="POST"
							onSubmit={async (e) => {
								e.preventDefault()
								if (newsletterEmail && !wrongFormatAlertVisible) {
									setIsLoading(true)
									try {
										await api('newsletterInvite', { email: newsletterEmail })
										setIsConfirmModalOpen(true)
										setIsNewsletterModalOpen(false)
										setIsLoading(false)
									} catch (err) {
										console.log('error', err)
									}
								}
							}}
						>
							<fieldset>
								<div className={style.textInputWrapper}>
									<TextInput
										name="email"
										type="email"
										onChange={(e) => setNewsletterEmail(e.target.value)}
										onBlur={(e) => checkEmail(e.target.value)}
										onFocus={() => {
											setWrongFormatAlertVisible(false)
										}}
										value={newsletterEmail}
										placeholder={translate('email.placeholder')}
									/>
									{wrongFormatAlertVisible && (
										<span className={style.textInputAlert}>
											<Translation tr="infoModal.forgotPassword.wrong.format.alert" />
										</span>
									)}
								</div>
								<div className={style.textInputWrapper}>
									<Button
										disabled={wrongFormatAlertVisible}
										buttonType="submit"
										label={isLoading ? <div className={style.loader} /> : translate('infoModal.newsletterModal.button')}
									/>
								</div>
							</fieldset>
						</form>
					}
				/>
			)}

			{isConfirmResendModalOpen && (
				<InfoModal
					title={translate('infoModal.verifyEmail.resendSuccess.title')}
					lead={translate('infoModal.verifyEmail.resendSuccess.subtitle')}
					imageUrl="/images/pes_modal_3.jpg"
					buttonLabel={translate('infoModal.verifyEmail.resendSuccess.close')}
					buttonType="button"
					isOpen={isConfirmResendModalOpen}
					imageAlternativeText={translate('infoModal.verifyEmail.resendSuccess.image')}
					onClick={() => setIsConfirmResendModalOpen(false)}
					closeModal={() => setIsConfirmResendModalOpen(false)}
				/>
			)}
			{isConfirmModalOpen && (
				<InfoModal
					title={translate('infoModal.newsletterSet.title')}
					lead={translate('infoModal.newsletterSet.subtitle')}
					imageUrl="/images/pes_modal_1.jpg"
					buttonLabel={translate('infoModal.newsletterSet.button')}
					buttonType="button"
					isOpen={isConfirmModalOpen}
					imageAlternativeText={translate('infoModal.newsletterSet.alt.image')}
					onClick={() => setIsConfirmModalOpen(false)}
					closeModal={() => setIsConfirmModalOpen(false)}
				/>
			)}
			<SearchModal
				isOpen={isSearchModalOpen}
				searchedText={searchValue}
				onChange={handleChange}
				closeModal={handleClose}
			/>
			{isMobileMenuOpen && (
				<div className={style.mobileMenuWrapper}>
					<Collapsible open={isMobileMenuOpen} hasLowerIndex>
						<MobileMenu close={() => setIsMobileMenuOpen(false)} isOpen={isMobileMenuOpen} />
					</Collapsible>
				</div>
			)}
			{!isMobileMenuOpen && loggedUser && !fullUse && !isVerifyPage && (
				<Container size="headerSize" disableGutters>
					<div className={clsx(style.notification, isUserMenuOpen && style.notificationHidden)}>
						<Message
							icon={<SvgWarningTriangle />}
							text={translate('header.notification.verify')}
							onClick={async () => {
								const result = await userApi('verifyEmail', {})
								console.log({ result })
								if (result.ok) {
									setIsConfirmResendModalOpen(true)
								}
							}}
						/>
					</div>
				</Container>
			)}
		</>
	)
})
